import { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Alert from '@cloudscape-design/components/alert';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Popover from '@cloudscape-design/components/popover';
import { INVISIBLE_CHARACTERS, formatUnicodeCode } from '@utils/invisibleCharacters';
import { useClipboard } from '@hooks/useClipboard';
import { getCharacterMetadata } from '@utils/characterMetadata';

const InvisibleCharacterLegendTool = () => {
  const [copySuccess, setCopySuccess] = useState('');
  const { copyToClipboard } = useClipboard();

  const handleCopyCharacter = async (char: string) => {
    const success = await copyToClipboard(char);
    if (success) {
      setCopySuccess(`Copied ${formatUnicodeCode(char.charCodeAt(0))} to clipboard!`);
      setTimeout(() => setCopySuccess(''), 3000);
    }
  };

  const renderCharacterCard = (char: typeof INVISIBLE_CHARACTERS[0]) => {
    const metadata = getCharacterMetadata(char.code);
    
    const popoverContent = (
      <div style={{ maxWidth: '350px' }}>
        <SpaceBetween size="m">
          {/* Character Information */}
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#0972d3' }}>Character Information</div>
            <div style={{ fontSize: '12px', color: '#5f6b7a' }}>
              <div><strong>Name:</strong> {char.name}</div>
              <div><strong>Code:</strong> {formatUnicodeCode(char.code)}</div>
              <div><strong>Category:</strong> {char.category}</div>
              <div><strong>Symbol:</strong> {char.symbol}</div>
            </div>
          </div>

          {/* Original Use */}
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#0972d3' }}>Original Use</div>
            <div style={{ fontSize: '12px', color: '#5f6b7a', lineHeight: '1.5' }}>
              {metadata.originalUse}
            </div>
          </div>

          {/* Security Risks */}
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#d13212' }}>Security Risks</div>
            <ul style={{ fontSize: '12px', color: '#5f6b7a', margin: '0', paddingLeft: '20px' }}>
              {metadata.securityRisks.map((risk, idx) => (
                <li key={idx}>{risk}</li>
              ))}
            </ul>
          </div>
        </SpaceBetween>
      </div>
    );

    return (
      <Popover
        key={char.code}
        size="large"
        position="top"
        triggerType="custom"
        content={popoverContent}
      >
        <div
          style={{
            border: '1px solid #e9ebed',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.15s',
            padding: '6px',
            textAlign: 'center',
            minHeight: '110px',
            minWidth: '110px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
          onMouseOver={(e: React.MouseEvent<HTMLDivElement>) => {
            e.currentTarget.style.backgroundColor = '#f0f8ff';
            e.currentTarget.style.borderColor = '#0972d3';
          }}
          onMouseOut={(e: React.MouseEvent<HTMLDivElement>) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#e9ebed';
          }}
          onClick={() => handleCopyCharacter(String.fromCharCode(char.code))}
        >
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {char.symbol}
          </div>
          <div style={{ fontSize: '11px', color: '#5f6b7a', lineHeight: '1.3', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {char.name}
          </div>
          <div style={{ fontSize: '10px', color: '#9ba7b6', marginTop: '2px' }}>
            {formatUnicodeCode(char.code)}
          </div>
        </div>
      </Popover>
    );
  };

  return (
    <Container
      header={
        <Header variant="h2">
          Invisible Character Legend
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Explanation */}
        <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '4px', lineHeight: '1.6', color: '#5f6b7a', fontSize: '14px' }}>
          <p><strong>What are invisible characters?</strong></p>
          <p>
            Invisible characters are Unicode characters that have little to no visual representation but still affect text processing. They include control characters (like line feeds and carriage returns), zero-width characters (like zero-width spaces and joiners), directional marks (for BiDi text), and various format characters. While these characters serve legitimate purposes in text processing and language support, they can be exploited for malicious purposes.
          </p>
          <p><strong>Why are they security risks?</strong></p>
          <p>
            Invisible characters pose security risks because they can be hidden in plain sight. Attackers can use them to:
            <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
              <li><strong>Obfuscate malicious content</strong> – Hide code or links within legitimate-looking text</li>
              <li><strong>Create homoglyph attacks</strong> – Mix scripts and directional marks to make look-alike domains or usernames</li>
              <li><strong>Bypass filters</strong> – Inject zero-width characters to evade spam or content filters</li>
              <li><strong>Perform phishing</strong> – Reverse domain names or filenames to disguise malicious intent</li>
              <li><strong>Fingerprint content</strong> – Embed hidden markers in AI-generated text for identification</li>
              <li><strong>Manipulate text processing</strong> – Exploit encoding confusion or BiDi rendering flaws</li>
            </ul>
          </p>
          <p>
            This tool helps you identify and analyze these hidden threats so you can understand and mitigate risks in your text, emails, URLs, and files.
          </p>
          
          <p style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #d0d0d0' }}><strong>25+ Security Detection Patterns</strong></p>
          <p>
            The associated "Analyze text on invisible Characters" tool detects 25+ different security patterns including:
            <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
              <li><strong>Zero-Width & Basic Invisible Characters</strong> – Detection of ZWSP, ZWNJ, ZWJ, format characters</li>
              <li><strong>Steganography & Hidden Data</strong> – Binary encoding patterns, data watermarking, interval-based encoding</li>
              <li><strong>Filter Evasion</strong> – Characters embedded within words to bypass filters</li>
              <li><strong>Phishing & Domain Spoofing</strong> – Punycode attacks, homograph exploits</li>
              <li><strong>BiDi Exploits</strong> – Right-to-Left Override attacks, filename spoofing</li>
              <li><strong>Zero-Width Chains</strong> – Consecutive invisible character sequences</li>
              <li><strong>Homoglyphs & Confusables</strong> – Cross-script character confusion</li>
              <li><strong>Script Mixing</strong> – Mixed Unicode script detection</li>
              <li><strong>Language-Specific Attacks</strong> – Case-folding and normalization bypasses</li>
              <li><strong>Math Symbol Substitution</strong> – Mathematical alphanumeric variants</li>
              <li><strong>Unicode Normalization Issues</strong> – Character form instability (NFC/NFD)</li>
              <li><strong>Control Character Exploits</strong> – ANSI escapes, backspace tricks</li>
              <li><strong>Pattern Detection</strong> – Repeating and positional patterns</li>
              <li><strong>Combining Diacritics</strong> – Zalgo text and excessive marks</li>
              <li><strong>Whitespace Abuse</strong> – Mixed space types, trailing spaces</li>
              <li><strong>Character Width Anomalies</strong> – Full-width/half-width variants</li>
              <li><strong>Look-alike Digits</strong> – Superscripts, subscripts, circled numbers</li>
              <li><strong>Quotation Mark Confusion</strong> – SQL injection risk via typographic quotes</li>
              <li><strong>Frequency Analysis</strong> – Entropy detection, Base64 identification</li>
              <li><strong>Domain-Specific Patterns</strong> – Email, IP, phone, credit card attacks</li>
              <li><strong>String Length Manipulation</strong> – Grapheme cluster bypasses</li>
              <li><strong>PUA Detection (2024)</strong> – Private Use Area characters with icon font distinction, steganography indicators</li>
              <li><strong>Fullwidth Exploitation (CVE-2024-4577, CVE-2024-34078)</strong> – Fullwidth characters that normalize dangerously, path traversal detection</li>
              <li><strong>Tag Block Character Smuggling (U+E0000–U+E007F)</strong> – LLM prompt injection detection, emoji tag abuse</li>
              <li><strong>Emoji Token Expansion LLM Attack (Wei et al., 2024)</strong> – ZWJ sequences, variation selectors, skin tone modifiers, token estimation</li>
            </ul>
          </p>
        </div>

        {/* Info Box with instruction */}
        <Alert type="info">
          Click any character to get more info or to copy it to your clipboard.
        </Alert>

        {/* Control Characters */}
        <ExpandableSection
          defaultExpanded={true}
          variant="container"
          headerText="Control Characters"
        >
          <SpaceBetween size="m">
            <div style={{ backgroundColor: '#f0f0f0', padding: '12px', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#5f6b7a' }}>
                <strong>Purpose &amp; Risk:</strong>
              </p>
              <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#5f6b7a' }}>
                <li><strong>Original Use:</strong> ASCII control codes for text formatting, device control</li>
                <li><strong>Zero-Width Effects:</strong> Invisible or render as non-printable</li>
                <li><strong>Abuse Cases:</strong> ANSI escapes, backspace tricks, terminal exploits</li>
                <li><strong>Risk Level:</strong> MEDIUM - Can manipulate text processing</li>
              </ul>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
              {INVISIBLE_CHARACTERS.filter(char => char.category === 'control').map((char) =>
                renderCharacterCard(char)
              )}
            </div>
          </SpaceBetween>
        </ExpandableSection>

        {/* Space Characters */}
        <ExpandableSection
          defaultExpanded={false}
          variant="container"
          headerText="Space Characters"
        >
          <SpaceBetween size="m">
            <div style={{ backgroundColor: '#f0f0f0', padding: '12px', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#5f6b7a' }}>
                <strong>Purpose &amp; Risk:</strong>
              </p>
              <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#5f6b7a' }}>
                <li><strong>Original Use:</strong> Unicode space variations for specific typographic contexts</li>
                <li><strong>Invisible Nature:</strong> Different widths but visually indistinguishable from regular space</li>
                <li><strong>Abuse Cases:</strong> Filter evasion, whitespace abuse, text manipulation</li>
                <li><strong>Risk Level:</strong> LOW-MEDIUM - Often overlooked by simple filters</li>
              </ul>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
              {INVISIBLE_CHARACTERS.filter(char => char.category === 'space').map((char) =>
                renderCharacterCard(char)
              )}
            </div>
          </SpaceBetween>
        </ExpandableSection>

        {/* Zero-Width Characters */}
        <ExpandableSection
          defaultExpanded={false}
          variant="container"
          headerText="Zero-Width Characters"
        >
          <SpaceBetween size="m">
            <div style={{ backgroundColor: '#f0f0f0', padding: '12px', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#5f6b7a' }}>
                <strong>Purpose &amp; Risk:</strong>
              </p>
              <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#5f6b7a' }}>
                <li><strong>Original Use:</strong> Control text flow, word/line breaking, character composition</li>
                <li><strong>Zero-Width Property:</strong> Contribute no width to line layout</li>
                <li><strong>Abuse Cases:</strong> Steganography, filter evasion, invisible data encoding</li>
                <li><strong>Risk Level:</strong> HIGH - Easiest to use for hidden data encoding</li>
              </ul>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
              {INVISIBLE_CHARACTERS.filter(char => char.category === 'zero-width').map((char) =>
                renderCharacterCard(char)
              )}
            </div>
          </SpaceBetween>
        </ExpandableSection>

        {/* Directional Characters */}
        <ExpandableSection
          defaultExpanded={false}
          variant="container"
          headerText="Directional Characters"
        >
          <SpaceBetween size="m">
            <div style={{ backgroundColor: '#f0f0f0', padding: '12px', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#5f6b7a' }}>
                <strong>Purpose &amp; Risk:</strong>
              </p>
              <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#5f6b7a' }}>
                <li><strong>Original Use:</strong> Bidirectional (BiDi) text control for mixed LTR/RTL scripts</li>
                <li><strong>Invisible Nature:</strong> Control text direction without visible effect</li>
                <li><strong>Abuse Cases:</strong> Trojan Source attacks, domain/filename spoofing, text reversal</li>
                <li><strong>Risk Level:</strong> HIGH - Can reverse visual order to hide malicious intent</li>
              </ul>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
              {INVISIBLE_CHARACTERS.filter(char => char.category === 'directional').map((char) =>
                renderCharacterCard(char)
              )}
            </div>
          </SpaceBetween>
        </ExpandableSection>

        {/* Combining Characters */}
        <ExpandableSection
          defaultExpanded={false}
          variant="container"
          headerText="Combining Characters (Zero-Width Diacritics)"
        >
          <SpaceBetween size="m">
            <div style={{ backgroundColor: '#f0f0f0', padding: '12px', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#5f6b7a' }}>
                <strong>Purpose &amp; Risk:</strong>
              </p>
              <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#5f6b7a' }}>
                <li><strong>Original Use:</strong> Diacritical marks (accents, umlauts) stacked above/below base characters</li>
                <li><strong>Zero-Width Property:</strong> Combine with preceding character, take no space</li>
                <li><strong>Abuse Cases:</strong> Zalgo text creation, filter evasion, obfuscation</li>
                <li><strong>Risk Level:</strong> MEDIUM - Excessive marks cause text corruption or bypass</li>
              </ul>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
              {INVISIBLE_CHARACTERS.filter(char => char.category === 'combining').map((char) =>
                renderCharacterCard(char)
              )}
            </div>
          </SpaceBetween>
        </ExpandableSection>

        {/* Deprecated Characters */}
        <ExpandableSection
          defaultExpanded={false}
          variant="container"
          headerText="Deprecated Characters (Including Tag Characters)"
        >
          <SpaceBetween size="m">
            <div style={{ backgroundColor: '#f0f0f0', padding: '12px', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#5f6b7a' }}>
                <strong>Purpose &amp; Risk:</strong>
              </p>
              <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#5f6b7a' }}>
                <li><strong>Original Use:</strong> Legacy characters for specific languages or format control (now obsolete)</li>
                <li><strong>Invisible Nature:</strong> Often invisible or render unpredictably</li>
                <li><strong>Tag Block Overlap:</strong> Includes U+E0000–U+E007F tag characters for emoji variants</li>
                <li><strong>Risk Level:</strong> MEDIUM-HIGH - Unpredictable rendering + LLM attack vectors</li>
              </ul>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
              {INVISIBLE_CHARACTERS.filter(char => char.category === 'deprecated').map((char) =>
                renderCharacterCard(char)
              )}
            </div>
          </SpaceBetween>
        </ExpandableSection>

        {/* ===== 2024 UNICODE THREATS SECTION ===== */}

        {/* Fullwidth Characters (CVE-2024-4577) */}
        <ExpandableSection
          defaultExpanded={false}
          variant="container"
          headerText="Fullwidth Characters (CVE-2024-4577, CVE-2024-34078)"
        >
          <SpaceBetween size="m">
            <div style={{ backgroundColor: '#ffe6f0', padding: '12px', borderRadius: '4px', lineHeight: '1.6', color: '#5f6b7a', fontSize: '13px' }}>
              <p style={{ marginTop: '0', marginBottom: '8px' }}>
                <strong>Dangerous fullwidth characters that normalize to ASCII equivalents AFTER validation passes.</strong> These bypass security filters through "normalization-after-validation" vulnerabilities.
              </p>
              <p style={{ marginBottom: '0' }}>
                Click characters to see details including normalization targets and associated CVEs.
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
              {[
                { code: 0xFF0E, name: 'Fullwidth Period', symbol: '．', category: 'fullwidth' },
                { code: 0xFF3C, name: 'Fullwidth Backslash', symbol: '＼', category: 'fullwidth' },
                { code: 0xFF0F, name: 'Fullwidth Solidus', symbol: '／', category: 'fullwidth' },
                { code: 0xFF1A, name: 'Fullwidth Colon', symbol: '：', category: 'fullwidth' },
                { code: 0xFF1C, name: 'Fullwidth Less-Than', symbol: '＜', category: 'fullwidth' },
                { code: 0xFF1E, name: 'Fullwidth Greater-Than', symbol: '＞', category: 'fullwidth' },
                { code: 0xFF0A, name: 'Fullwidth Asterisk', symbol: '＊', category: 'fullwidth' },
                { code: 0xFF1F, name: 'Fullwidth Question Mark', symbol: '？', category: 'fullwidth' },
              ].map((char) => renderCharacterCard(char as typeof INVISIBLE_CHARACTERS[0]))}
            </div>
          </SpaceBetween>
        </ExpandableSection>

        {/* Complex Emoji Sequences */}
        <ExpandableSection
          defaultExpanded={false}
          variant="container"
          headerText="Complex Emoji Sequences (Token Expansion, Wei et al. 2024)"
        >
          <SpaceBetween size="m">
            <div style={{ backgroundColor: '#ffe6e6', padding: '12px', borderRadius: '4px', lineHeight: '1.6', color: '#5f6b7a', fontSize: '13px' }}>
              <p style={{ marginTop: '0', marginBottom: '8px' }}>
                <strong>Emoji that expand into many tokens in LLMs, causing "token segmentation bias".</strong> These can bypass AI safety filters through unexpected tokenization patterns.
              </p>
              <p style={{ marginBottom: '0' }}>
                Examples: ZWJ sequences, skin tone variations, family emoji, profession sequences, flags.
              </p>
            </div>
            <div style={{ backgroundColor: '#f0f0f0', padding: '12px', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#5f6b7a' }}>
                <strong>Attack Mechanism:</strong>
              </p>
              <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#5f6b7a' }}>
                <li><strong>Simple emoji:</strong> 1 token (👋)</li>
                <li><strong>Skin tone modifiers:</strong> +1 token (👋🏻 = 2 tokens)</li>
                <li><strong>ZWJ sequences:</strong> 5-6 tokens (👨‍💻 = 5-6 tokens)</li>
                <li><strong>Family chains:</strong> 10+ tokens (👨‍👩‍👧‍👦 = 10+ tokens)</li>
                <li><strong>Bypass rate:</strong> 12-75% depending on LLM model</li>
              </ul>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
              {[
                { code: 0x1F44B, name: 'Waving Hand', symbol: '👋', category: 'zero-width' },
                { code: 0x1F44B, name: 'Waving Hand + Skin Tone', symbol: '👋🏻', category: 'zero-width' },
                { code: 0x1F468, name: 'Man Technologist (ZWJ)', symbol: '👨‍💻', category: 'zero-width' },
                { code: 0x1F3F3, name: 'Flag: Rainbow', symbol: '🏳️‍🌈', category: 'zero-width' },
                { code: 0x1F1FA, name: 'Flag: United States', symbol: '🇺🇸', category: 'zero-width' },
                { code: 0x1F468, name: 'Family (ZWJ Chain)', symbol: '👨‍👩‍👧‍👦', category: 'zero-width' },
              ].map((char) => renderCharacterCard(char as typeof INVISIBLE_CHARACTERS[0]))}
            </div>
          </SpaceBetween>
        </ExpandableSection>

        {/* PUA Sample Characters */}
        <ExpandableSection
          defaultExpanded={false}
          variant="container"
          headerText="PUA Sample Characters (Icon Fonts & Custom Use)"
        >
          <SpaceBetween size="m">
            <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px', lineHeight: '1.6', color: '#5f6b7a', fontSize: '13px' }}>
              <p style={{ marginTop: '0', marginBottom: '8px' }}>
                <strong>Sample Private Use Area characters from known icon font ranges.</strong> Legitimate uses in UI design, but can be exploited for steganography.
              </p>
              <p style={{ marginBottom: '0' }}>
                Font Awesome (U+F000–F2FF) and Material Design (U+E000–E3FF) are the most common legitimate uses.
              </p>
            </div>
            <div style={{ backgroundColor: '#f0f0f0', padding: '12px', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#5f6b7a' }}>
                <strong>Legitimate vs Suspicious Context:</strong>
              </p>
              <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#5f6b7a' }}>
                <li><strong>✅ Legitimate:</strong> &lt;1% PUA density + in CSS/HTML styling context</li>
                <li><strong>✅ Legitimate:</strong> Clustered icon fonts in web UI components</li>
                <li><strong>⚠️ Suspicious:</strong> 1-5% PUA density mixed with normal text</li>
                <li><strong>🔴 Dangerous:</strong> 5-20% PUA density + isolated from content</li>
                <li><strong>🔴 Critical:</strong> &gt;20% PUA density + pattern matching steganography</li>
              </ul>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
              {[
                { code: 0xF001, name: 'Font Awesome: Music', symbol: '', category: 'space' },
                { code: 0xF002, name: 'Font Awesome: Search', symbol: '', category: 'space' },
                { code: 0xF04B, name: 'Font Awesome: Play', symbol: '', category: 'space' },
                { code: 0xF050, name: 'Font Awesome: Pause', symbol: '', category: 'space' },
                { code: 0xE000, name: 'Material Design: Custom', symbol: '', category: 'space' },
                { code: 0xE001, name: 'Material Design: Custom', symbol: '', category: 'space' },
              ].map((char) => renderCharacterCard(char as typeof INVISIBLE_CHARACTERS[0]))}
            </div>
          </SpaceBetween>
        </ExpandableSection>

        {/* Tag Block Characters */}
        <ExpandableSection
          defaultExpanded={false}
          variant="container"
          headerText="Tag Block Characters (U+E0000–U+E007F)"
        >
          <SpaceBetween size="m">
            <div style={{ backgroundColor: '#f0e6ff', padding: '12px', borderRadius: '4px', lineHeight: '1.6', color: '#5f6b7a', fontSize: '13px' }}>
              <p style={{ marginTop: '0', marginBottom: '8px' }}>
                <strong>Invisible tag characters used for language/variant tagging.</strong> Legitimate use in emoji flags, but can be exploited for LLM prompt injection and steganography.
              </p>
              <p style={{ marginBottom: '0' }}>
                Range: U+E0000–U+E007F (128 characters). Invisible in most text editors but detectable through analysis.
              </p>
            </div>
            <div style={{ backgroundColor: '#f0f0f0', padding: '12px', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#5f6b7a' }}>
                <strong>Attack Vectors &amp; Detection:</strong>
              </p>
              <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#5f6b7a' }}>
                <li><strong>Prompt Injection:</strong> Hide malicious instructions in LLM prompts</li>
                <li><strong>Token Expansion DoS:</strong> Multiple tag chars cause token bloat</li>
                <li><strong>Content Fingerprinting:</strong> Embed invisible markers for tracking</li>
                <li><strong>Emoji Flag Composition:</strong> Legitimate use combining regional indicators</li>
                <li><strong>Detection:</strong> Tool reveals all tag characters in analysis</li>
              </ul>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
              {[
                { code: 0xE0000, name: 'Tag: Null', symbol: '⁪', category: 'control' },
                { code: 0xE0001, name: 'Tag: Start of Heading', symbol: '⁪', category: 'control' },
                { code: 0xE0020, name: 'Tag: Space', symbol: '⁪', category: 'control' },
                { code: 0xE0041, name: 'Tag: Latin Capital A', symbol: '⁪', category: 'control' },
                { code: 0xE0051, name: 'Tag: Latin Capital Q', symbol: '⁪', category: 'control' },
                { code: 0xE007E, name: 'Tag: Tilde', symbol: '⁪', category: 'control' },
              ].map((char) => renderCharacterCard(char as typeof INVISIBLE_CHARACTERS[0]))}
            </div>
          </SpaceBetween>
        </ExpandableSection>

        {/* 2024 UNICODE ATTACKS - DEEP DIVE */}
        <ExpandableSection
          defaultExpanded={false}
          variant="container"
          headerText="2024 Threat Analysis: Deep Dive - Normalization-After-Validation Vulnerabilities"
        >
          <SpaceBetween size="m">
            <div style={{ backgroundColor: '#e6f3ff', padding: '12px', borderRadius: '4px', lineHeight: '1.6', color: '#5f6b7a', fontSize: '13px' }}>
              <p style={{ marginTop: '0', marginBottom: '8px' }}>
                <strong>For interactive examples of other 2024 threats, see the sections above:</strong>
              </p>
              <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#5f6b7a' }}>
                <li><strong>Emoji Token Expansion Attacks:</strong> See "Complex Emoji Sequences" section (Wei et al., 2024)</li>
                <li><strong>Fullwidth Character Exploitation:</strong> See "Fullwidth Characters" section (CVE-2024-4577, CVE-2024-34078)</li>
                <li><strong>Tag Block Character Smuggling:</strong> See "Tag Block Characters" section (U+E0000–U+E007F)</li>
              </ul>
              <p style={{ marginTop: '8px', marginBottom: '0' }}>
                <strong>This section provides a detailed technical analysis of one critical 2024 vulnerability:</strong>
              </p>
            </div>

            {/* Normalization Vulnerabilities - Deep Dive */}
            <div style={{ backgroundColor: '#e6f3ff', padding: '12px', borderRadius: '4px', lineHeight: '1.6', color: '#5f6b7a', fontSize: '13px' }}>
              <p style={{ marginTop: '0', marginBottom: '8px' }}>
                <strong>🚨 Unicode Normalization-After-Validation Vulnerabilities</strong>
              </p>
              <p style={{ marginBottom: '8px' }}>
                The "normalization-after-validation" pattern is a critical vulnerability in security implementations across multiple platforms and applications. Characters pass validation checks, then normalize to dangerous forms AFTER the validation has passed.
              </p>

              <p style={{ marginBottom: '8px' }}>
                <strong>Affected CVEs:</strong>
              </p>
              <ul style={{ margin: '0 0 8px 0', paddingLeft: '20px' }}>
                <li><strong>CVE-2024-34078:</strong> HTML sanitizer bypass in web applications</li>
                <li><strong>CVE-2024-43093:</strong> Android security vulnerability in path validation</li>
                <li><strong>CVE-2023-52081:</strong> Path filter bypass in file system operations</li>
              </ul>

              <p style={{ marginBottom: '8px' }}>
                <strong>Attack Flow:</strong>
              </p>
              <ol style={{ margin: '0 0 8px 0', paddingLeft: '20px' }}>
                <li><strong>Attacker Input:</strong> U+2024 (․ - One Dot Leader)</li>
                <li><strong>Validation Phase:</strong> Character passes checks (not detected as dangerous)</li>
                <li><strong>Normalization Phase:</strong> U+2024 normalizes to U+002E (.)</li>
                <li><strong>Processing Phase:</strong> Application processes it as literal dot</li>
                <li><strong>Result:</strong> Path traversal succeeds: ../../etc/passwd</li>
              </ol>

              <p style={{ marginBottom: '8px' }}>
                <strong>Dangerous Lookalike Characters & Their Normalizations:</strong>
              </p>
              <ul style={{ margin: '0 0 8px 0', paddingLeft: '20px' }}>
                <li><strong>U+2024 (․)</strong> → U+002E (.) - One Dot Leader to Full Stop</li>
                <li><strong>U+FF0E (．)</strong> → U+002E (.) - Fullwidth Full Stop to Full Stop</li>
                <li><strong>U+FF1A (：)</strong> → U+003A (:) - Fullwidth Colon to Colon</li>
                <li><strong>U+2044 (⁄)</strong> → U+002F (/) - Fraction Slash to Solidus</li>
                <li><strong>U+2215 (∕)</strong> → U+002F (/) - Division Slash to Solidus</li>
              </ul>

              <p style={{ marginBottom: '8px' }}>
                <strong>Root Cause Analysis:</strong>
              </p>
              <p style={{ marginBottom: '8px' }}>
                The vulnerability exists because security filters operate on the <strong>unnormalized input</strong>, while the application processes the <strong>normalized form</strong>. This creates a semantic gap:
              </p>
              <ul style={{ margin: '0 0 8px 0', paddingLeft: '20px' }}>
                <li><strong>Filter sees:</strong> U+2024 ․ (not dangerous)</li>
                <li><strong>Application sees:</strong> U+002E . (dangerous in context)</li>
              </ul>

              <p style={{ marginBottom: '8px' }}>
                <strong>Prevention Strategy (Correct Order):</strong>
              </p>
              <ol style={{ margin: '0 0 8px 0', paddingLeft: '20px' }}>
                <li><strong>Step 1: Normalize First</strong> - Convert all forms to NFC (or NFKC for maximum compatibility)</li>
                <li><strong>Step 2: Validate Normalized Form</strong> - Check the normalized version against blacklists</li>
                <li><strong>Step 3: Sanitize</strong> - Remove or escape dangerous characters</li>
                <li><strong>Step 4: Process</strong> - Use the validated, normalized input</li>
              </ol>

              <p style={{ marginBottom: '0' }}>
                <strong>Implementation Example (Pseudocode):</strong>
              </p>
              <div style={{ backgroundColor: '#f9f9f9', padding: '8px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px', marginTop: '4px', overflow: 'auto' }}>
                <pre style={{ margin: '0' }}>
{`// CORRECT: Normalize FIRST, validate SECOND
input = userInput.normalize('NFC')
if (isPathTraversal(input)) reject()
if (containsDangerous(input)) reject()
process(input)

// WRONG: Validate FIRST, normalize SECOND
if (isPathTraversal(userInput)) reject()
input = userInput.normalize('NFC')  // Too late!
process(input)`}
                </pre>
              </div>
            </div>

            <Alert type="warning">
              <strong>Critical Security Best Practice:</strong> Always normalize user input BEFORE validation, not after. Unicode normalization should be the first step in your security processing pipeline. Validate the normalized form, never the raw input.
            </Alert>

            <Alert type="info">
              <strong>Detection in this Tool:</strong> This Unicode Security Analyzer detects normalization-after-validation patterns by checking if characters would normalize to dangerous forms. Use the detection results to validate your application's input processing order.
            </Alert>
          </SpaceBetween>
        </ExpandableSection>

        {/* PUA DEEP DIVE - BACKGROUND & SECURITY IMPLICATIONS */}
        <ExpandableSection
          defaultExpanded={false}
          variant="container"
          headerText="Deep Dive: Private Use Area (PUA) - Background & Security Implications"
        >
          <SpaceBetween size="m">
            <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px', lineHeight: '1.6', color: '#5f6b7a', fontSize: '13px' }}>
              <p style={{ marginTop: '0', marginBottom: '8px' }}>
                <strong>For interactive examples of PUA characters, see the "PUA Sample Characters" section above.</strong>
              </p>
              <p style={{ marginTop: '0', marginBottom: '0' }}>
                This section provides comprehensive background on the 137,468 Private Use Area characters and their security implications.
              </p>
            </div>

            <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px', lineHeight: '1.6', color: '#5f6b7a', fontSize: '13px' }}>
              <p style={{ marginTop: '0', marginBottom: '8px' }}><strong>What are PUA Characters?</strong></p>
              <p>
                Private Use Area (PUA) characters are 137,468 Unicode code points that have <strong>no standardized meaning</strong>. They are permanently reserved by the Unicode Consortium for custom implementations and private agreements between applications.
              </p>
              <p style={{ marginBottom: '0' }}>
                <strong>Three PUA Ranges:</strong>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                  <li><strong>BMP PUA:</strong> U+E000–U+F8FF (6,400 characters)</li>
                  <li><strong>Supplementary PUA-A:</strong> U+F0000–U+FFFFD (65,536 characters)</li>
                  <li><strong>Supplementary PUA-B:</strong> U+100000–U+10FFFD (65,536 characters)</li>
                </ul>
              </p>
            </div>

            <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px', lineHeight: '1.6', color: '#5f6b7a', fontSize: '13px' }}>
              <p style={{ marginTop: '0', marginBottom: '8px' }}><strong>Legitimate Uses:</strong></p>
              <ul style={{ margin: '0', paddingLeft: '20px' }}>
                <li><strong>Icon Fonts</strong> – Font Awesome (U+F000–U+F2FF), Material Design (U+E000–U+E3FF), Ionicons, and others map UI icons to PUA</li>
                <li><strong>CJK Gaiji</strong> – Rare East Asian characters (Chinese/Japanese/Korean rare characters, historical variants, proprietary characters)</li>
                <li><strong>Custom Scripts</strong> – Constructed languages, fictional writing systems, experimental typography</li>
                <li><strong>Specialized Symbols</strong> – Company branding, proprietary notation, internal corporate systems</li>
                <li><strong>Legacy Systems</strong> – Hardware manufacturers, embedded systems, older applications with custom encodings</li>
              </ul>
            </div>

            <Alert type="warning">
              High PUA density (&gt;5% of text) in unexpected contexts strongly suggests malicious use for steganography or data exfiltration. Icon fonts should be limited to specific web/design contexts and CSS styling, never mixed with regular text content.
            </Alert>

            <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px', lineHeight: '1.6', color: '#5f6b7a', fontSize: '13px' }}>
              <p style={{ marginTop: '0', marginBottom: '8px' }}><strong>🔴 CRITICAL SECURITY RISKS:</strong></p>
              <ul style={{ margin: '0', paddingLeft: '20px' }}>
                <li><strong>Steganography &amp; Data Hiding</strong> – 137K+ characters for invisible data encoding, perfect for covert communication</li>
                <li><strong>Filter Evasion</strong> – Replace normal chars with PUA equivalents to bypass content filters, spam detectors, keyword filters</li>
                <li><strong>Command Injection</strong> – Inject commands using PUA chars that normalize to dangerous characters after validation</li>
                <li><strong>Phishing &amp; Spoofing</strong> – Create visually identical content with different meaning using lookalike PUA chars</li>
                <li><strong>Fingerprinting</strong> – Embed invisible markers for user tracking, AI content watermarking, document tracking</li>
                <li><strong>Semantic Gap Attacks</strong> – Applications recognize PUA differently (fonts, rendering, processing order)</li>
              </ul>
            </div>

            <div style={{ backgroundColor: '#f0f0f0', padding: '12px', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#5f6b7a' }}>
                <strong>📊 Risk Assessment - When is PUA Density Suspicious?</strong>
              </p>
              <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#5f6b7a' }}>
                <li><strong>✅ LOW RISK</strong>: &lt;1% PUA density + in HTML/CSS context (likely icon fonts)</li>
                <li><strong>✅ LOW RISK</strong>: Clustered icon fonts in web UI components (buttons, navigation, icons)</li>
                <li><strong>⚠️ MEDIUM RISK</strong>: 1-5% PUA density + mixed with normal text</li>
                <li><strong>⚠️ MEDIUM RISK</strong>: PUA in unexpected contexts (emails, chat messages, documents)</li>
                <li><strong>🔴 HIGH RISK</strong>: 5-20% PUA density + isolated from normal content</li>
                <li><strong>🔴 CRITICAL RISK</strong>: &gt;20% PUA density + pattern matching known steganography techniques</li>
              </ul>
            </div>

            <Alert type="info">
              <strong>Detection in this Tool:</strong> This Unicode Security Analyzer detects PUA characters in your input and analyzes their density, clustering patterns, and distribution. It distinguishes between legitimate icon fonts and suspicious steganography usage. Use the detection results to identify when PUA usage is appropriate vs. malicious.
            </Alert>
          </SpaceBetween>
        </ExpandableSection>

        {/* Success Message */}
        {copySuccess && (
          <Alert type="success" dismissible onDismiss={() => setCopySuccess('')}>
            {copySuccess}
          </Alert>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default InvisibleCharacterLegendTool;
